'use client';

import { useCreateGiftQuote } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';

/**
 * Asset shape passed to the quote endpoint. Only USDC gifts carry a fee;
 * outcome-token gifts transfer the exact mint amount.
 */
export type GiftQuoteAsset = {
  kind: 'usdc';
  amountBaseUnits: string;
};
export interface GiftQuoteInput {
  asset: GiftQuoteAsset | null;
  recipientPlatform: 'morfi' | 'x';
  recipientHandle: string;
}

/**
 * Server-computed fee preview for the gift modal. Identical shape to the
 * backend `QuoteResponseSchema` data envelope.
 */
export interface GiftQuoteData {
  feeUsdcBaseUnits: string;
  netToVaultBaseUnits: string;
  totalDeductedFromSenderUsdcBaseUnits: string;
  feeBps: number;
  vaultExists: boolean;
  vaultClaimed: boolean;
  vaultCycleIndex: number;
  nextCycleIndex: number;
  willInitNextCycle: boolean;
  estimatedTxFeeLamports: string;
}
export type GiftQuoteStatus = 'idle' | 'loading' | 'success' | 'error';
export interface UseGiftQuoteResult {
  data: GiftQuoteData | null;
  status: GiftQuoteStatus;
  error: string | null;
}
const DEBOUNCE_MS = 300;
interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

// ALLOW-UNKNOWN: type guard narrowing arbitrary thrown values from the SDK.
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

// ALLOW-UNKNOWN: SDK rejections may be Error, ApiError envelope, or raw object.
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (!isObject(err)) return 'Failed to fetch quote';
  if (typeof err.message === 'string') return err.message;
  // ALLOW-UNKNOWN: narrowing inner.error envelope from SDK rejection shape
  const inner = (err as {
    error?: unknown;
  }).error;
  if (isObject(inner)) {
    const env = inner as ApiErrorEnvelope;
    if (typeof env.message === 'string') return env.message;
    if (typeof env.code === 'string') return env.code;
  }
  return 'Failed to fetch quote';
}

/**
 * Debounced gift fee-preview hook (FR-007, FR-040).
 *
 * Calls `POST /v1/gifts/quote` 300 ms after `input` stabilises. Returns
 * `{ data, status, error }`:
 *   - `idle` when input is incomplete (missing asset, empty handle, zero amount)
 *   - `loading` while the request is in flight
 *   - `success` with `data` populated
 *   - `error` with a human-readable message
 *
 * Stale-response handling: only the latest request id wins, so rapid retypes
 * do not flicker the UI with intermediate results.
 */
interface SettledQuote {
  signature: string;
  data: GiftQuoteData | null;
  status: 'success' | 'error';
  error: string | null;
}
export function useGiftQuote(input: GiftQuoteInput): UseGiftQuoteResult {
  const [settled, setSettled] = useState<SettledQuote | null>(null);
  const requestIdRef = useRef<number>(0);
  const createGiftQuoteMutation = useCreateGiftQuote();
  const mutateAsync = createGiftQuoteMutation.mutateAsync;
  const {
    asset,
    recipientPlatform,
    recipientHandle
  } = input;
  const handleTrimmed = recipientHandle.trim();
  // Depend on primitive asset fields so a parent-created object cannot restart
  // the debounce on every render.
  const assetKind = asset?.kind ?? null;
  const amountBaseUnits = asset?.amountBaseUnits ?? '';
  const assetKey = `${assetKind ?? ''}:${amountBaseUnits}`;
  const amountZero = amountBaseUnits === '0';
  // Derived during render: incomplete input is never an async state, it is always idle.
  const isQuotable = handleTrimmed.length > 0 && assetKind !== null && !amountZero;
  // Identity of the current quotable input. A settled result only applies while
  // this signature matches; any input change falls back to `loading`.
  const signature = `${recipientPlatform}${handleTrimmed}${assetKey}`;
  useEffect(() => {
    if (!isQuotable || assetKind === null) return;
    const parsedAsset: GiftQuoteAsset = {
      kind: assetKind,
      amountBaseUnits
    };
    const myRequestId = requestIdRef.current + 1;
    requestIdRef.current = myRequestId;
    const timer = setTimeout((): void => {
      void (async (): Promise<void> => {
        try {
          const result = await mutateAsync({
            recipientPlatform,
            recipientHandle: handleTrimmed,
            asset: parsedAsset
          });
          if (requestIdRef.current !== myRequestId) return;
          // v2 mutation returns the parsed `data` envelope directly.
          setSettled({
            signature,
            data: (result as {
              data?: GiftQuoteData;
            }).data ?? result as GiftQuoteData,
            status: 'success',
            error: null
          });
        } catch (err: unknown) {
          // SILENT-CATCH: quote failure is surfaced to the UI via the derived `error` and renders as an inline message.
          if (requestIdRef.current !== myRequestId) return;
          setSettled({
            signature,
            data: null,
            status: 'error',
            error: extractErrorMessage(err)
          });
        }
      })();
    }, DEBOUNCE_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [amountBaseUnits, assetKind, handleTrimmed, isQuotable, mutateAsync, recipientPlatform, signature]);
  if (!isQuotable) return {
    data: null,
    status: 'idle',
    error: null
  };
  if (settled?.signature === signature) {
    return {
      data: settled.data,
      status: settled.status,
      error: settled.error
    };
  }
  return {
    data: null,
    status: 'loading',
    error: null
  };
}